import { useEffect, useRef } from 'react'

import type { RealtimeChannel } from '@/lib/realtime'
import { useRealtimeManager } from '@/lib/realtime'

export interface RealtimeTypingEvent {
  conversationId: string
  userId: string
  typing: boolean
}

export interface UseChatRealtimeOptions {
  /** Channel to subscribe to; omit on the chat list. */
  channel?: RealtimeChannel
  /** Activity or conversation id matching the channel. */
  id?: string | null
  /** Refetch or merge when a matching message/notification arrives. */
  onUpdate: () => void
  /**
   * Merge a realtime message into route state. Return `true` when the frame
   * was handled (deduped/append); `false` falls back to a targeted refetch.
   */
  onActivityMessage?: (activityId: string, message: unknown) => boolean
  onPrivateMessage?: (conversationId: string, message: unknown) => boolean
  /** Ephemeral typing event for the open private conversation. */
  onTyping?: (event: RealtimeTypingEvent) => void
  /** Refresh on user-channel notifications too (chat list unread hints). */
  listenToNotifications?: boolean
  /** Fallback cadence while the WebSocket is not ready (D-042/§7). */
  pollIntervalMs?: number
}

const DEFAULT_POLL_MS = 15_000

/**
 * Single realtime + REST fallback subscription for chats.
 *
 * - One app-wide `RealtimeManager` connection handles auth, backoff and
 *   channel reference counts. Matching `activity.message` / `private.message`
 *   frames are appended by the route hook (deduped by id), otherwise the hook
 *   schedules a targeted refetch; the route remains the source of display
 *   state.
 * - Pub/Sub is best effort: reconnect, focus and visibility trigger a targeted
 *   refetch. While the socket is unavailable the same hook refetches every
 *   15 s only while the document is visible.
 * - Screens never talk to the socket directly.
 */
export function useChatRealtime({
  channel,
  id = null,
  onUpdate,
  onActivityMessage,
  onPrivateMessage,
  onTyping,
  listenToNotifications = false,
  pollIntervalMs = DEFAULT_POLL_MS,
}: UseChatRealtimeOptions): void {
  const manager = useRealtimeManager()
  const updateRef = useRef(onUpdate)
  const activityRef = useRef(onActivityMessage)
  const privateRef = useRef(onPrivateMessage)
  const typingRef = useRef(onTyping)

  useEffect(() => {
    updateRef.current = onUpdate
    activityRef.current = onActivityMessage
    privateRef.current = onPrivateMessage
    typingRef.current = onTyping
  }, [onActivityMessage, onPrivateMessage, onTyping, onUpdate])

  useEffect(() => {
    const unsubscribeFrames = manager.addFrameListener((frame) => {
      if (frame.type === 'activity.message') {
        const covered =
          channel === 'activity'
            ? Boolean(id) && frame.activityId === id
            : channel === undefined
        if (!covered) return
        const handled =
          activityRef.current?.(frame.activityId, frame.message) === true
        if (!handled) updateRef.current()
        return
      }

      if (frame.type === 'private.message') {
        const covered =
          channel === 'conversation'
            ? Boolean(id) && frame.conversationId === id
            : channel === undefined
        if (!covered) return
        const handled =
          privateRef.current?.(frame.conversationId, frame.message) === true
        if (!handled) updateRef.current()
        return
      }

      if (frame.type === 'typing') {
        if (channel !== 'conversation' || !id) return
        if (frame.conversationId !== id) return
        typingRef.current?.({
          conversationId: frame.conversationId,
          userId: frame.userId,
          typing: frame.typing,
        })
        return
      }

      if (frame.type === 'activity.participant') {
        if (channel === 'activity' && id && frame.activityId === id) {
          updateRef.current()
        }
        return
      }

      if (frame.type === 'join_request' && !channel) {
        updateRef.current()
        return
      }

      if (frame.type === 'notification' && listenToNotifications) {
        updateRef.current()
      }
    })

    const unsubscribeResync = manager.addResyncListener(() => {
      // Reconnected: re-auth/resubscribe happened in the client, then the
      // route closes any gap with an authoritative REST read.
      updateRef.current()
    })

    if (channel && id) manager.subscribe(channel, id)

    return () => {
      if (channel && id) manager.unsubscribe(channel, id)
      unsubscribeFrames()
      unsubscribeResync()
    }
  }, [channel, id, listenToNotifications, manager])

  useEffect(() => {
    function onFocusOrVisibility() {
      if (document.visibilityState === 'visible') updateRef.current()
    }

    function refreshIfFallback() {
      if (manager.getStatus() !== 'ready') updateRef.current()
    }

    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') refreshIfFallback()
    }, pollIntervalMs)

    document.addEventListener('visibilitychange', onFocusOrVisibility)
    window.addEventListener('focus', onFocusOrVisibility)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onFocusOrVisibility)
      window.removeEventListener('focus', onFocusOrVisibility)
    }
  }, [manager, pollIntervalMs])
}
